import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useHubTagTemplatesContext } from '../../../contexts/HubTagTemplatesContext/HubTagTemplatesContext';
import type { HubTagTemplate } from '../../../dorian-shared/types/branch/HubTagTemplate';
import { showToast } from '../../ui/utils';
import {
  getNameError, getContentError, validateTemplates,
} from './tagTemplateValidation';

type LocalTemplate = HubTagTemplate & { localKey: string };

function toLocalTemplates(templates: HubTagTemplate[]): LocalTemplate[] {
  return templates.map((t) => ({ ...t, localKey: t.uuid ?? String(t.id) }));
}

export function useTagTemplatesModal(onHide: () => void) {
  const {
    templates, isLoading, fetchTemplates, createTemplate, updateTemplate, deleteTemplate,
  } = useHubTagTemplatesContext();

  // usedInStory can change outside this modal when a Hub node is saved,
  // so refresh on open to keep the delete guard in sync with actual usage.
  useEffect(() => {
    const abortController = new AbortController();
    fetchTemplates(abortController, { includeUsage: true }).catch(() => {});
    return () => {
      abortController.abort();
    };
  }, [fetchTemplates]);

  const prevTemplatesRef = useRef(templates);
  const [localTemplates, setLocalTemplates] = useState<LocalTemplate[]>(() => toLocalTemplates(templates));
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null);

  if (prevTemplatesRef.current !== templates) {
    prevTemplatesRef.current = templates;
    setLocalTemplates(toLocalTemplates(templates));
  }

  const allNames = useMemo(
    () => localTemplates.map((t) => t.name),
    [localTemplates],
  );

  const templateErrors = useMemo(
    () => localTemplates.map((t, i) => ({
      nameError: getNameError(t.name, allNames, i),
      contentError: getContentError(t.text, t.iconId),
    })),
    [localTemplates, allNames],
  );

  const hasAnyError = templateErrors.some((e) => !!e.nameError || !!e.contentError);

  const handleChange = useCallback((index: number, updated: HubTagTemplate) => {
    setLocalTemplates((prev) => prev.map((t, i) => (i === index ? { ...updated, localKey: t.localKey } : t)));
    setIsDirty(true);
  }, []);

  const handleAdd = useCallback(() => {
    const newTemplate: LocalTemplate = {
      name: '',
      text: null,
      color: '#808080',
      iconId: null,
      localKey: uuidv4(),
    };
    setLocalTemplates((prev) => [...prev, newTemplate]);
    setIsDirty(true);
  }, []);

  const handleDeleteRequest = useCallback((index: number) => {
    setDeleteConfirmIndex(index);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (deleteConfirmIndex == null) return;
    const template = localTemplates[deleteConfirmIndex];
    if (template.id != null) {
      try {
        await deleteTemplate(template.id);
        showToast({ textMessage: 'Tag template deleted', variant: 'success' });
      } catch (err) {
        const backendError = (err as { response?: { data?: { error?: string } } })
          ?.response?.data?.error;
        showToast({ textMessage: backendError ?? 'Failed to delete tag template' });
        setDeleteConfirmIndex(null);
        return;
      }
    }
    setLocalTemplates((prev) => prev.filter((_, i) => i !== deleteConfirmIndex));
    setIsDirty(true);
    setDeleteConfirmIndex(null);
  }, [deleteConfirmIndex, localTemplates, deleteTemplate]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmIndex(null);
  }, []);

  const handleSave = useCallback(async () => {
    const error = validateTemplates(localTemplates);
    if (error) {
      showToast({ textMessage: error });
      return;
    }

    setIsSaving(true);
    try {
      await Promise.all(localTemplates.map((t) => {
        const payload = {
          name: t.name,
          text: t.text,
          color: t.color,
          iconId: t.iconId,
        };
        return t.id != null
          ? updateTemplate(t.id, payload)
          : createTemplate(payload);
      }));
      showToast({ textMessage: 'Tag templates saved', variant: 'success' });
      setIsDirty(false);
      onHide();
    } catch {
      showToast({ textMessage: 'Failed to save tag templates' });
    } finally {
      setIsSaving(false);
    }
  }, [localTemplates, createTemplate, updateTemplate, onHide]);

  const isDisabled = isLoading || isSaving;
  const deleteTemplateName = deleteConfirmIndex != null
    ? localTemplates[deleteConfirmIndex]?.name || 'unnamed'
    : '';

  return {
    localTemplates,
    templateErrors,
    isLoading,
    isSaving,
    isDirty,
    isDisabled,
    hasAnyError,
    deleteConfirmIndex,
    deleteTemplateName,
    handleChange,
    handleAdd,
    handleDeleteRequest,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleSave,
  };
}
